import NurseRosterList from "@components/feature/rosterTable/row_NurseRosterList";
import { useEffect, useState, type SetStateAction } from "react";
import type * as type from "@vTypes";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";

import { images } from "@img/imagesMap";
import type { CellViolationInfo } from "@/services/validation";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import { nurseGroupSortKey } from "@utils/common-util.ts";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";

export default function TableBody_team({
  team,
  nurses,
  copyNurses,
  setCopyNurses,
  fullNurseSchema,
  setFullNurseSchema,
  availableShifts,
  shiftLookupMaps,
  currentSubmittedList,
  isEditMode,
  selectedShift,
  modDayShift,
  saveNurseSequence,
  // setFullNurseSchemaAddPreceptor,
  fullNurseSchemaAddPreceptor,
  cellViolationMap,
  statsColumnShiftIds,
  statsDisplayShifts,
  prevTailData,
  showPerShiftStats = false,
  selectIdForSideprofile,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId = null,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
}: {
  team: type.FetchTeams;
  nurses: any;
  setCopyNurses: (value: any) => void;
  fullNurseSchema: any[];
  // setFullNurseSchema : ()=>void;
  setFullNurseSchema: React.Dispatch<SetStateAction<type.FullNurseSchema[]>>;
  availableShifts: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<type.availableShifts>;
  currentSubmittedList: any;
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  saveNurseSequence: () => void;
  // setFullNurseSchemaAddPreceptor : ()=>void;
  fullNurseSchemaAddPreceptor: any[];
  prevTailData?: PrevTailData | null;
  cellViolationMap?: Map<string, CellViolationInfo>;
  statsColumnShiftIds?: string[];
  statsDisplayShifts?: type.availableShifts[];
  showPerShiftStats?: boolean;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  scheduleYear?: number;
  scheduleMonth?: number;
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  currentGroupId?: string | null;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
}) {
  const [isExpendedContents, setIsExpendedContents] = useState<boolean>(true);

  useEffect(() => {
    const clickWindow = (e: Event) => {
      const clickTarget = e.target as HTMLElement;
      if (!clickTarget.closest(".preceptorMark")) {
        document
          .querySelectorAll(".preceptorGroup")
          .forEach((tr) => tr.classList.remove("click"));
      }
    };
    window.addEventListener("click", clickWindow);
    return () => {
      window.removeEventListener("click", clickWindow);
    };
  }, []);

  const teamNurses = nurses.filter((nurse) => nurse != null);

  const nurseList = teamNurses.map((nurse, index) => {
    const isSubmitted = currentSubmittedList?.includes(nurse?.nurse_id);
    const nextNurse = teamNurses[index + 1];
    const roleGroupBottomBorder =
      nextNurse != null &&
      nurseGroupSortKey(nurse) !== nurseGroupSortKey(nextNurse);

    const nurseKey = String(nurse?.nurse_id ?? nurse?.id ?? "");
    const globalNurseIndex =
      Array.isArray(copyNurses) && nurseKey.length > 0
        ? copyNurses.findIndex(
            (n) =>
              n != null &&
              String(n?.nurse_id ?? n?.id ?? "") === nurseKey
          )
        : -1;
    const nursesIndexForRow =
      globalNurseIndex >= 0 ? globalNurseIndex : index;

    return (
      <NurseRosterList
        key={String(nurse?.nurse_id ?? nurse?.id ?? `nurse-row-${index}`)}
        isEditMode={isEditMode}
        nursesIndex={nursesIndexForRow}
        nurse={nurse}
        availableShifts={availableShifts}
        shiftLookupMaps={shiftLookupMaps}
        isSubmitted={isSubmitted}
        modDayShift={modDayShift}
        selectedShift={selectedShift}
        cellViolationMap={cellViolationMap}
        statsColumnShiftIds={statsColumnShiftIds}
        statsDisplayShifts={statsDisplayShifts}
        prevTailData={prevTailData}
        showPerShiftStats={showPerShiftStats}
        roleGroupBottomBorder={roleGroupBottomBorder}
        selectIdForSideprofile={selectIdForSideprofile}
        scheduleYear={scheduleYear}
        scheduleMonth={scheduleMonth}
        schedulePayloadYear={schedulePayloadYear}
        schedulePayloadMonth={schedulePayloadMonth}
        currentGroupId={currentGroupId}
        isEmergencyPickMode={isEmergencyPickMode}
        onEmergencySlotPick={onEmergencySlotPick}
        emergencySlotPick={emergencySlotPick}
        isEmergencyBulkPickMode={isEmergencyBulkPickMode}
        onEmergencyBulkPick={onEmergencyBulkPick}
        emergencyBulkPeriodPick={emergencyBulkPeriodPick}
        emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
        emergencyReplacementCandidateNurseId={
          emergencyReplacementCandidateNurseId
        }
        emergencyPostApplyHighlight={emergencyPostApplyHighlight}
      />
    );
  });

  return (
    <div className="tbody tbody_team">
      <div
        className={`tbody_team_header ${isExpendedContents ? "" : "tbody_team_header--closed"}`}
      >
        <div className="nurseCount padding_hover_bg_vertical">
          <img
            src={images.user}
            alt=""
            className=""
            style={{ height: "1em" }}
          />
          {nurses.length}
        </div>

        <div className="tbody_team_teamName">
          <button
            type="button"
            style={{ width: "100%", height: "100%" }}
            onClick={() => {
              setIsExpendedContents((prev) => !prev);
            }}
          >
            {team.team_name}
          </button>
        </div>
      </div>

      {isExpendedContents && (
        <div className="tbody_scollbox">
          {nurseList.some((item) => item !== null) ? (
            nurseList
          ) : (
            <div className="comp_tr comp_tr--oneLine">
              그룹이 설정된 인원이 없습니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
